import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import React, { useContext, useState, createContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { useFocusTrap } from '$hooks/events';

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */
const noop = async () => { };

const ModalContext = createContext({
    isModalOpen: false,
    handleModal: noop,
    modalNode: null
});

/**
 * @function useModal
 * useModal hook, stores modal DOM node and modal state
 */
const useModal = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalNode, setModalNode] = useState();

    const handleModal = (content = false) => {
        setIsModalOpen(!isModalOpen);
        if (content) {
            setModalNode(content);
        }
    };

    return {
        isModalOpen,
        handleModal,
        modalNode
    };
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component ModalProvider
 * @props (see propTypes)
 */
const ModalProvider = ({ children }) => {
    const { isModalOpen, handleModal, modalNode, setIsModalOpen } = useModal();

    return (
        <ModalContext.Provider value={{ isModalOpen, handleModal, modalNode, setIsModalOpen }}>
            <Modal />
            {children}
        </ModalContext.Provider>
    );
};

/**
 * @component Modal
 * @props (see propTypes)
 */
const Modal = () => {
    const { modalNode, isModalOpen, handleModal } = useContext(ModalContext);
    const { elementRef: modalContainerRef } = useFocusTrap(isModalOpen);
    const lastFocusedElementRef = useRef();

    useEffect(() => {
        if (!isModalOpen) { return; }

        const containerRef = modalContainerRef?.current;

        const handleKeydown = (event) => event.keyCode === 27 /* Escape */ && handleModal();

        if (containerRef) {
            disableBodyScroll(containerRef, { allowTouchMove: () => true });
            document.addEventListener('keydown', handleKeydown);

            // save last focused element before the modal opened
            lastFocusedElementRef.current = document.activeElement;
        }

        return () => {
            if (containerRef) {
                enableBodyScroll(containerRef);
                document.removeEventListener('keydown', handleKeydown);

                // restore focus
                lastFocusedElementRef.current.focus();
            }
        };
    }, [isModalOpen]);


    if (isModalOpen) {
        return ReactDOM.createPortal(
            <div
                aria-modal='true'
                ref={modalContainerRef}
                role='dialog'
                tabIndex='-1'>
                {modalNode}
            </div>,
            document.querySelector('#dialogs')
        );
    } else {
        return null;
    }
};

const ModalConsumer = ModalContext.Consumer;

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ModalProvider.propTypes = {
};

export {
    ModalConsumer,
    ModalContext,
    ModalProvider,
};
